import axios from "axios";
import { OlympicRegion, OlympicRegistration, OlympicRegistrationData } from "../../types/olympic";

const BASE_URL = "";

const olympicAxios = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const API_PATHS = {
  REGISTER: "/api/olympic/register",
  ADMIN_EVENTS: "/api/olympic/admin/events",
  ADMIN_REGISTRATIONS: "/api/olympic/admin/registrations",
};

export interface OlympicRegistrationSummary {
  region: OlympicRegion;
  churchCount: number;
  teamCount: number;
  totalAmount: number;
}

export interface OlympicAdminRegistration extends OlympicRegistrationData {
  churchName?: string;
  churchCode?: string;
}

export const olympicApi = {
  register: async (
    eventId: string,
    churchId: string,
    registration: OlympicRegistration
  ): Promise<{ success: boolean; message?: string; data?: OlympicRegistrationData }> => {
    try {
      const response = await olympicAxios.post<{ success: boolean; message?: string; data?: OlympicRegistrationData }>(
        API_PATHS.REGISTER,
        { eventId, churchId, ...registration }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error registering Olympic:", error);
      throw new Error(error.response?.data?.error || error.message || "등록에 실패했습니다.");
    }
  },

  getRegistration: async (eventId: string, churchId: string): Promise<OlympicRegistrationData | null> => {
    try {
      const response = await olympicAxios.get<{ success: boolean; data: OlympicRegistrationData | null }>(
        `${API_PATHS.REGISTER}/${eventId}/${churchId}`
      );
      return response.data?.data ?? null;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      console.error("Error fetching Olympic registration:", error);
      throw new Error(error.response?.data?.error || error.message || "등록 데이터를 불러오는데 실패했습니다.");
    }
  },

  getRegistrationsByEvent: async (eventId: string): Promise<OlympicAdminRegistration[]> => {
    try {
      const response = await olympicAxios.get<{ success: boolean; data: OlympicAdminRegistration[] }>(
        `${API_PATHS.ADMIN_EVENTS}/${eventId}/registration-data`
      );
      return response.data?.data ?? [];
    } catch (error: any) {
      console.error("Error fetching Olympic registrations:", error);
      throw new Error(error.response?.data?.error || error.message || "목록을 불러오는데 실패했습니다.");
    }
  },

  getSummary: async (eventId: string): Promise<OlympicRegistrationSummary[]> => {
    try {
      const response = await olympicAxios.get<{ success: boolean; data: OlympicRegistrationSummary[] }>(
        `${API_PATHS.ADMIN_EVENTS}/${eventId}/summary`
      );
      return response.data?.data ?? [];
    } catch (error: any) {
      console.error("Error fetching Olympic summary:", error);
      throw new Error(error.response?.data?.error || error.message || "요약 정보를 불러오는데 실패했습니다.");
    }
  },

  deleteRegistration: async (eventId: string, churchId: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await olympicAxios.delete<{ success: boolean; message?: string }>(
        `${API_PATHS.ADMIN_REGISTRATIONS}/${eventId}/${churchId}`
      );
      return response.data;
    } catch (error: any) {
      console.error("Error deleting Olympic registration:", error);
      throw new Error(error.response?.data?.error || error.message || "삭제에 실패했습니다.");
    }
  },

  exportRegionExcelUrl: (eventId: string, region?: OlympicRegion): string => {
    const q = region ? `?region=${region}` : "";
    return `${API_PATHS.ADMIN_EVENTS}/${eventId}/export${q}`;
  },

  exportChurchExcelUrl: (eventId: string, churchId: string): string => {
    return `${API_PATHS.ADMIN_EVENTS}/${eventId}/export/${churchId}`;
  },

  syncReceipts: async (
    eventId: string
  ): Promise<{
    success: boolean;
    message?: string;
    synced?: number;
    created?: number;
    updated?: number;
    errors?: string[];
  }> => {
    try {
      const response = await olympicAxios.post<{
        success: boolean;
        message?: string;
        synced?: number;
        created?: number;
        updated?: number;
        errors?: string[];
      }>(`${API_PATHS.ADMIN_EVENTS}/${eventId}/sync-receipts`);
      return response.data;
    } catch (error: any) {
      console.error("Error syncing Olympic receipts:", error);
      throw new Error(
        error.response?.data?.error || error.message || "영수증 동기화에 실패했습니다."
      );
    }
  },
};
